/**
 * Self-service MFA recovery for someone who lost the authenticator app AND every recovery code.
 *
 *   /mfa-recovery           start: e-mail + password (Identity e-mails the owner a link)
 *   /mfa-recovery/confirm   the first e-mailed link: confirm the request, then wait
 *   /mfa-recovery/complete  the link e-mailed when the wait is over: remove two-factor authentication
 *
 * The two link paths are part of the contract with authentication-service (its e-mails link to them).
 * Both link pages act only on an explicit button press, never on load, so a mail scanner that opens
 * links cannot confirm or complete a request for the owner.
 */
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import { AuthLayout } from "@/components/layout";
import { Alert, Button, buttonClass, ErrorAlert, Field, Form, Input, PasswordInput } from "@/components/ui";
import { auth } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { ApiError } from "@/lib/http";

const LINK_CLASS = "text-brand-700 hover:underline dark:text-brand-500";

const isUnavailable = (err: unknown) => err instanceof ApiError && err.reason === "MFA_RECOVERY_DISABLED";
const isDeadLink = (err: unknown) =>
  err instanceof ApiError && (err.reason === "TOKEN_INVALID_OR_EXPIRED" || err.status === 422);

/** "about 5 hours" / "about 40 minutes" for a Retry-After. */
export function waitText(seconds: number): string {
  if (seconds < 90) return "a minute";
  if (seconds < 55 * 60) return `about ${Math.round(seconds / 60)} minutes`;
  const hours = Math.round(seconds / 3600);
  return hours === 1 ? "about an hour" : `about ${hours} hours`;
}

function Unavailable() {
  return (
    <Alert tone="warning">
      Recovering an account yourself isn&apos;t available right now. Please contact support and we&apos;ll
      help you verify it&apos;s you.
    </Alert>
  );
}

function DeadLink() {
  return (
    <Alert tone="danger">
      This link no longer works. The request may have been canceled, replaced by a newer one, or it may have
      expired. You can{" "}
      <Link to="/mfa-recovery" className="font-medium underline">
        start again
      </Link>
      .
    </Alert>
  );
}

function BackToSignIn() {
  return (
    <p className="mt-6 text-center text-sm">
      <Link to="/login" className={LINK_CLASS}>
        Back to sign in
      </Link>
    </p>
  );
}

// ── 1. start ────────────────────────────────────────────────────────────────
export function MfaRecoveryStartPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const start = useMutation({ mutationFn: () => auth.startMfaRecovery(email.trim(), password) });
  return (
    <AuthLayout
      title="Lost your authenticator and recovery codes?"
      subtitle="Use this only if you can no longer get a code from your authenticator app or use a recovery code."
    >
      {start.isSuccess ? (
        <div className="space-y-4">
          <Alert tone="success">
            If that e-mail and password are right and the account has two-factor authentication on, we&apos;ve
            sent a confirmation link to {email.trim()}.
          </Alert>
          <ol className="list-decimal space-y-2 pl-5 text-sm text-fg-muted">
            <li>Open the link in that e-mail to confirm that you asked for this.</li>
            <li>
              Then we wait before anything changes: 24 hours, or longer if you&apos;re using a device or
              network we haven&apos;t seen on your account. This is what stops someone else doing it to you.
            </li>
            <li>When the wait is over we e-mail you again with a link to finish.</li>
          </ol>
          <p className="text-sm text-fg-muted">
            Didn&apos;t ask for this? Don&apos;t confirm it. Changing your password cancels any request.
          </p>
        </div>
      ) : (
        <Form
          className="space-y-4"
          error={start.error}
          onSubmit={(e) => {
            e.preventDefault();
            start.mutate();
          }}
        >
          {isUnavailable(start.error) ? <Unavailable /> : <ErrorAlert error={start.error} />}
          <Field label="E-mail" name="email" required>
            {(p) => (
              <Input
                {...p}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- the form is this screen's only task, so focus starts in its first field (PHP-3348)
                autoFocus
              />
            )}
          </Field>
          <Field
            label="Password"
            name="password"
            required
            hint="We need your password and access to this mailbox."
          >
            {(p) => (
              <PasswordInput
                {...p}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>
          <Button type="submit" className="w-full" loading={start.isPending}>
            Start recovery
          </Button>
        </Form>
      )}
      <BackToSignIn />
    </AuthLayout>
  );
}

// ── 2. confirm the mailbox ─────────────────────────────────────────────────
export function MfaRecoveryConfirmPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const confirm = useMutation({ mutationFn: () => auth.confirmMfaRecovery(token) });
  return (
    <AuthLayout title="Confirm MFA recovery">
      {!token && <Alert tone="danger">This link is incomplete.</Alert>}
      {token && !confirm.isSuccess && (
        <div className="space-y-4">
          <p className="text-sm text-fg-muted">
            Someone asked to remove two-factor authentication from your account. If that was you, confirm it
            below. Nothing changes yet: there is a waiting period first, and we&apos;ll e-mail you again when
            you can finish. If it wasn&apos;t you, don&apos;t confirm it, and change your password.
          </p>
          {isUnavailable(confirm.error) ? (
            <Unavailable />
          ) : isDeadLink(confirm.error) ? (
            <DeadLink />
          ) : (
            <ErrorAlert error={confirm.error} />
          )}
          <Button className="w-full" loading={confirm.isPending} onClick={() => confirm.mutate()}>
            Yes, this was me
          </Button>
        </div>
      )}
      {confirm.isSuccess && (
        <div className="space-y-4 text-center">
          <CheckCircle2 className="mx-auto size-10 text-brand-600" aria-hidden />
          {confirm.data.ready ? (
            <p className="text-sm text-fg-muted">
              Confirmed. The waiting period is already over, so we&apos;ve e-mailed you the link to finish
              recovery.
            </p>
          ) : (
            <p className="text-sm text-fg-muted">
              Confirmed. You can finish recovery from{" "}
              <strong className="text-fg">{formatDateTime(confirm.data.available_at)}</strong>. We&apos;ll
              e-mail you the link then.
            </p>
          )}
        </div>
      )}
      <BackToSignIn />
    </AuthLayout>
  );
}

// ── 3. complete ────────────────────────────────────────────────────────────
export function MfaRecoveryCompletePage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const complete = useMutation({ mutationFn: () => auth.completeMfaRecovery(token) });
  const error = complete.error;
  return (
    <AuthLayout title="Finish MFA recovery">
      {!token && <Alert tone="danger">This link is incomplete.</Alert>}
      {token && !complete.isSuccess && (
        <div className="space-y-4">
          <p className="text-sm text-fg-muted">
            This removes two-factor authentication from your account and deletes your recovery codes. Every
            device is signed out. You can then sign in with your password and set up a new authenticator.
          </p>
          {isUnavailable(error) ? (
            <Unavailable />
          ) : isDeadLink(error) ? (
            <DeadLink />
          ) : error instanceof ApiError && error.reason === "MFA_RECOVERY_WAITING" ? (
            <Alert tone="warning">
              The waiting period isn&apos;t over yet. Please try again in{" "}
              {waitText(error.retryAfterSeconds ?? 3600)}.
            </Alert>
          ) : error instanceof ApiError && error.reason === "MFA_RECOVERY_NOT_ELIGIBLE" ? (
            <Alert tone="danger">
              This account can&apos;t recover two-factor authentication right now. Please contact support.
            </Alert>
          ) : (
            <ErrorAlert error={error} />
          )}
          <Button
            variant="danger"
            className="w-full"
            loading={complete.isPending}
            onClick={() => complete.mutate()}
          >
            Remove two-factor authentication
          </Button>
        </div>
      )}
      {complete.isSuccess && (
        <div className="space-y-4 text-center">
          <CheckCircle2 className="mx-auto size-10 text-brand-600" aria-hidden />
          <p className="text-sm text-fg-muted">
            Two-factor authentication is off and every device was signed out. Sign in with your password, then
            set up a new authenticator in Settings.
          </p>
          <Link to="/login" className={buttonClass("primary", "md", "w-full")}>
            Sign in
          </Link>
        </div>
      )}
      {!complete.isSuccess && <BackToSignIn />}
    </AuthLayout>
  );
}
